import React, { useEffect, useState } from 'react';
import { BRAND_NAME } from '../../theme/brand';
import {
    Box,
    VStack,
    HStack,
    Card,
    CardBody,
    Text,
    Button,
    Table,
    Thead,
    Tbody,
    Tr,
    Th,
    Td,
    TableContainer,
    Grid,
    Alert,
    AlertDescription,
    Tag,
    TabPanel,
    Tabs,
    TabList,
    Tab,
    TabPanels,
    Modal,
    ModalOverlay,
    ModalContent,
    ModalHeader,
    ModalBody,
    ModalFooter,
    ModalCloseButton,
    Input,
    InputGroup,
    useDisclosure,
    useToast,
    Spinner,
    Radio,
    RadioGroup
} from '@chakra-ui/react';
import { FiCopy, FiCheck, FiEye, FiTarget, FiFileText, FiPlus } from 'react-icons/fi';
import { fetchWrapper } from '../../helpers';
import { getUserFromToken } from '../../components/authHelper';

export const WebsiteTrackingCode = ({ canCreateProperty = false }) => {
    const [copiedSection, setCopiedSection] = useState(null);
    const [expandedSections, setExpandedSections] = useState({
        react: false,
        vue: false,
        wordpress: false,
    });
    const [list, setList] = useState([]);
    const [properties, setProperties] = useState([]);
    const [selectedProperty, setSelectedProperty] = useState(null);
    const [websiteUrl, setWebsiteUrl] = useState('');
    const [isCreating, setIsCreating] = useState(false);
    const [isLoadingProperties, setIsLoadingProperties] = useState(false);
    const { isOpen, onOpen, onClose } = useDisclosure();
    const toast = useToast();
    const user = getUserFromToken();

    const id = user.customer_id;

    const loadList = async (id) => {
        const url = `${process.env.REACT_APP_API_URL}/customer/by-id`;
        const data = await fetchWrapper.get(url);
        setList(data.data);
    };

    const loadProperties = async () => {
        setIsLoadingProperties(true);
        try {
            const url = `${process.env.REACT_APP_API_URL}/customer-portal/proprty-domain-list`;
            const data = await fetchWrapper.get(url);

            if (data?.property_domain) {
                setProperties(data.property_domain);
                if (data.property_domain.length > 0 && !selectedProperty) {
                    setSelectedProperty(data.property_domain[0]?.property_id);
                }
            }
        } catch (err) {
            console.error('Failed to load properties', err);
            toast({
                title: 'Error loading properties',
                description: err?.message || 'Something went wrong',
                status: 'error',
                duration: 3000,
                isClosable: true,
            });
        } finally {
            setIsLoadingProperties(false);
        }
    };

    const handleCreateProperty = async () => {
        if (!websiteUrl.trim()) return;

        const domainName = websiteUrl

        setIsCreating(true);
        try {
            const url = `${process.env.REACT_APP_API_URL}/customer-portal/add-proprty-domain`;
            const data = await fetchWrapper.post(url, { domain_name: domainName });

            toast({
                title: 'Property created',
                description: 'Domain added successfully',
                status: 'success',
                duration: 3000,
                isClosable: true,
            });

            setWebsiteUrl('');
            onClose();
            await loadProperties();
        } catch (err) {
            console.error('Failed to create property', err);
            toast({
                title: 'Error creating property',
                description: err?.message || 'Something went wrong',
                status: 'error',
                duration: 3000,
                isClosable: true,
            });
        } finally {
            setIsCreating(false);
        }
    };

    useEffect(() => {
        loadList(id);
        loadProperties();
    // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);

    const copyToClipboard = (text, section) => {
        const fallbackCopy = (text) => {
            const textArea = document.createElement('textarea');
            textArea.value = text;
            textArea.style.position = 'fixed';
            textArea.style.left = '-999999px';
            document.body.appendChild(textArea);
            textArea.focus();
            textArea.select();
            try {
                document.execCommand('copy');
            } catch (err) {
                console.error('Fallback: Could not copy text', err);
            }
            document.body.removeChild(textArea);
        };

        if (navigator.clipboard && navigator.clipboard.writeText) {
            navigator.clipboard.writeText(text).catch(() => {
                fallbackCopy(text);
            });
        } else {
            fallbackCopy(text);
        }

        setCopiedSection(section);
        setTimeout(() => setCopiedSection(null), 2000);
    };

    const toggleSection = (section) => {
        setExpandedSections(prev => ({
            ...prev,
            [section]: !prev[section]
        }));
    };

    // Use selected property's property_id for code snippets, fallback to YOUR_PROPERTY_ID
    const currentPropertyId = selectedProperty?.property_id || 'YOUR_PROPERTY_ID';
    const trackerUrl = `${process.env.REACT_APP_EVENT_TRACKER_URL}`;

    const basicInstall = `<script src="${trackerUrl}"
  data-property-id="${currentPropertyId}">
</script>`;

    const installWithUser = `<script src="${trackerUrl}"
  data-property-id="${currentPropertyId}"
  data-contact-no="923181018154">
</script>`;

    const debugMode = `<script src="${trackerUrl}"
  data-property-id="${currentPropertyId}"
  data-debug>
</script>`;

    const identifyOnce = `EventTracker.identify('9231***18154', {
  email: 'user@example.com',
  name: 'John Doe'
});`;

    const overrideSingleEvent = `EventTracker.track('purchase', {
  order_id: 1234,
  amount: 99.99
}, '92300***6543');`;

    return (
        <VStack spacing={8} align="stretch" pb={10} mt={4}>
            {/* Property Management Section */}
            <Box>
                <Card shadow="sm" bg="var(--brand-tint)" borderColor="blue.200" borderWidth={1}>
                    <CardBody p={6}>
                        <HStack justify="space-between" align="flex-start" mb={4}>
                            <Box flex={1}>
                                <Text fontSize="xl" fontWeight="400" mb={2}>Property Management</Text>
                                <Text color="gray.700">
                                    {properties.length === 0
                                        ? "Create properties to get started with event tracking. Each property represents a website or application with a unique Property ID for identification."
                                        : `You have ${properties.length} ${properties.length === 1 ? 'property' : 'properties'} configured. Select a property to view its tracking code, or add a new property to track another website.`}
                                </Text>
                            </Box>
                            {canCreateProperty && (
                            <Button
                                onClick={onOpen}
                                size={"sm"}
                                bg="var(--brand-primary)"
                                color="white"
                                _hover={{ bg: 'var(--brand-primary-hover)' }}
                                leftIcon={<FiPlus />}
                                ml={4}
                                flexShrink={0}
                            >
                                {properties.length === 0 ? 'Create Property' : 'Add Property'}
                            </Button>
                            )}
                        </HStack>

                        {/* Property List */}
                        {isLoadingProperties ? (
                            <Box textAlign="center" py={6}>
                                <Spinner color="var(--brand-primary)" />
                                <Text mt={2} color="gray.600">Loading properties...</Text>
                            </Box>
                        ) : properties.length > 0 ? (
                            <VStack spacing={3} align="stretch" mt={4}>
                                <Text fontWeight="400" color="gray.700">Your Properties:</Text>

                                <RadioGroup
                                    value={selectedProperty?.property_id || selectedProperty || ''}
                                    onChange={(val) => {
                                        const property = properties.find(p => (p.property_id || p.id) === val);
                                        if (property) setSelectedProperty(property);
                                    }}
                                    
                                >
                                    <VStack spacing={3} align="stretch">
                                        {properties.map((property) => {
                                            const propertyId = property.property_id;
                                            const isSelected = (selectedProperty?.property_id || selectedProperty?.id) === propertyId;

                                            return (
                                                <Card
                                                    key={propertyId}
                                                    shadow="sm"
                                                    cursor="pointer"
                                                    transition="all 0.2s"
                                                    borderWidth={isSelected ? 2 : 1}
                                                    borderColor={isSelected ? 'var(--brand-primary)' : 'gray.200'}
                                                    bg={isSelected ? 'white' : undefined}
                                                    _hover={{ borderColor: isSelected ? 'var(--brand-primary)' : 'gray.300' }}
                                                    onClick={() => setSelectedProperty(property)}
                                                >
                                                    <CardBody p={4}>
                                                        <HStack justify="space-between">
                                                            <HStack spacing={3} flex={1}>
                                                                <Radio
                                                                    value={propertyId}
                                                                    colorScheme="blue"
                                                                    isChecked={isSelected}
                                                                />
                                                                <Box>
                                                                    <Text fontWeight="400" color="gray.800">
                                                                        {property.domain_name}
                                                                    </Text>
                                                                    {property.property_id && (
                                                                        <Text fontFamily="mono" fontSize="sm" color="gray.600">
                                                                            Property ID: {property.property_id}
                                                                        </Text>
                                                                    )}
                                                                </Box>
                                                            </HStack>
                                                            <Tag
                                                                size={"sm"}
                                                                bg={isSelected ? 'var(--brand-primary)' : 'gray.400'}
                                                                color="white"
                                                            >
                                                                {isSelected ? 'Selected' : 'Select'}
                                                            </Tag>
                                                        </HStack>
                                                    </CardBody>
                                                </Card>
                                            );
                                        })}
                                    </VStack>
                                </RadioGroup>
                            </VStack>
                        ) : (
                            <Alert mt={4} bg="white" borderColor="gray.300" borderWidth={1} borderRadius="md">
                                <AlertDescription>
                                    No properties created yet. Click "Create Property" to get started with event tracking.
                                </AlertDescription>
                            </Alert>
                        )}
                    </CardBody>
                </Card>
            </Box>

            {/* Create Property Modal */}
            <Modal isOpen={isOpen} onClose={onClose}>
                <ModalOverlay />
                <ModalContent>
                    <ModalHeader>Create New Property</ModalHeader>
                    <ModalCloseButton />
                    <ModalBody>
                        <Text color="gray.600" mb={4}>
                            Enter your website domain to create a new property. You'll receive a unique Property ID to use in your tracking code.
                        </Text>
                        <Text fontWeight="medium" mb={2}>Website URL *</Text>
                        <InputGroup>
                            <Input
                                placeholder="example.com"
                                value={websiteUrl}
                                onChange={(e) => setWebsiteUrl(e.target.value.replace(/^https?:\/\//i, ''))}
                            />
                        </InputGroup>
                        <Text fontSize="sm" color="gray.500" mt={2}>
                            Enter domain only (e.g., example.com or demo.example.com)
                        </Text>
                    </ModalBody>
                    <ModalFooter>
                        <Button
                            variant="outline"
                            mr={3}
                            onClick={() => {
                                onClose();
                                setWebsiteUrl('');
                            }}
                        >
                            Cancel
                        </Button>
                        <Button
                            bg="var(--brand-primary)"
                            color="white"
                            _hover={{ bg: 'var(--brand-primary-hover)' }}
                            onClick={handleCreateProperty}
                            isDisabled={!websiteUrl.trim()}
                            isLoading={isCreating}
                        >
                            Create Property
                        </Button>
                    </ModalFooter>
                </ModalContent>
            </Modal>

            {/* Quick Start Section */}
            <Box>
                <Text fontSize="20px" fontWeight="600" color="#000000" my="0px" >Quick Start</Text>
                <Box h="1px" bg="#EAEAEA" mb={4} />
                <Text color="gray.700" mb={2}>
                    Get started in less than 60 seconds. Add this single line of code to your website and start tracking events immediately.
                </Text>
                <Text color="gray.700" mb={4}>
                    Track user behavior, sessions, and conversions on your website using the {BRAND_NAME} Web Event Tracker. Capture events like page views, clicks, form submissions, and e-commerce actions with minimal setup.
                </Text>
            </Box>

            {/* Installation Variants */}
            <VStack spacing={4} align="stretch">
                {/* Basic Installation */}
                <Box className='box-style-card' shadow="sm">
                    <Box >
                        <HStack mb={3} spacing={2}>
                            <Tag bg="#00c951" size={"sm"} color="white">Recommended</Tag>
                            <Text fontWeight="semibold">Basic Installation</Text>
                        </HStack>
                        <Text color="gray.700" mb={4}>
                            Standard setup for anonymous event tracking without user identification.
                        </Text>
                        <Box shadow="sm" bg="#1E1E1E" color="white" position="relative" borderRadius={"md"}>
                            <Button
                                position="absolute"
                                top={3}
                                right={3}
                                size="sm"
                                bg="whiteAlpha.200"
                                color="white"
                                _hover={{ bg: 'whiteAlpha.300' }}
                                leftIcon={copiedSection === 'basic' ? <Box as={FiCheck} /> : <Box as={FiCopy} />}
                                onClick={() => copyToClipboard(basicInstall, 'basic')}
                            >
                                {copiedSection === 'basic' ? 'Copied!' : 'Copy'}
                            </Button>
                            <Box pt={10} pb={4} px={4} >
                                <Box as="pre" fontSize="sm" overflowX="auto" >
                                    <code>{basicInstall}</code>
                                </Box>
                            </Box>
                        </Box>
                    </Box>
                </Box>

                {/* With User Identification */}
                <Box className='box-style-card' shadow="sm">
                    <Box >
                        <Text fontWeight="semibold" mb={3}>With User Identification</Text>
                        <Text color="gray.700" mb={4}>
                            Include user identification directly in the script tag for logged-in users.
                        </Text>
                        <Box shadow="sm" bg="#1E1E1E" color="white" position="relative" borderRadius={"md"}>
                            <Button
                                position="absolute"
                                top={3}
                                right={3}
                                size="sm"
                                bg="whiteAlpha.200"
                                color="white"
                                _hover={{ bg: 'whiteAlpha.300' }}
                                leftIcon={copiedSection === 'with-user' ? <Box as={FiCheck} /> : <Box as={FiCopy} />}
                                onClick={() => copyToClipboard(installWithUser, 'with-user')}
                            >
                                {copiedSection === 'with-user' ? 'Copied!' : 'Copy'}
                            </Button>
                            <Box pt={10} pb={4} px={4}>
                                <Box as="pre" fontSize="sm" overflowX="auto">
                                    <code>{installWithUser}</code>
                                </Box>
                            </Box>
                        </Box>
                    </Box>
                </Box>

                {/* Debug Mode */}
                <Box className='box-style-card' shadow="sm" borderWidth={2} borderColor="orange.200">
                    <Box >
                        <HStack mb={3} spacing={2}>
                            <Tag bg="#ff6900" size={"sm"} color="white">Development</Tag>
                            <Text fontWeight="semibold">Debug Mode</Text>
                        </HStack>
                        <Text color="gray.700" mb={4}>
                            Enable detailed console logging for troubleshooting and testing during development.
                        </Text>
                        <Box shadow="sm" bg="#1E1E1E" color="white" position="relative" borderRadius={"md"}>
                            <Button
                                position="absolute"
                                top={3}
                                right={3}
                                size="sm"
                                bg="whiteAlpha.200"
                                color="white"
                                _hover={{ bg: 'whiteAlpha.300' }}
                                leftIcon={copiedSection === 'debug' ? <Box as={FiCheck} /> : <Box as={FiCopy} />}
                                onClick={() => copyToClipboard(debugMode, 'debug')}
                            >
                                {copiedSection === 'debug' ? 'Copied!' : 'Copy'}
                            </Button>
                            <Box pt={10} pb={4} px={4}>
                                <Box as="pre" fontSize="sm" overflowX="auto">
                                    <code>{debugMode}</code>
                                </Box>
                            </Box>
                        </Box>
                        <Alert mt={4} bg="#fff7ed" borderColor="orange.200" borderWidth={1} borderRadius={"md"}>
                            <AlertDescription color="orange.800">
                                ⚠️ <Text as="strong">Warning:</Text> Debug mode enables console logs for troubleshooting and testing. Disable before deploying to production.
                            </AlertDescription>
                        </Alert>
                    </Box>
                </Box>
            </VStack>

            {/* Automatic Event Tracking */}
            <Box>
                <Text fontSize="20px" fontWeight="600" color="#000000" my="0px" >Automatic Event Tracking</Text>
                <Box h="1px" bg="#EAEAEA" mb={4} />
                <Text color="gray.700" mb={6}>
                    The tracker automatically captures common user interactions on every page load without requiring manual implementing tracking code.
                </Text>

                <Grid templateColumns={{ base: '1fr', md: 'repeat(3, 1fr)' }} gap={4} mb={6}>
                    <Card shadow="md" bg="var(--brand-tint)" borderColor="blue.200" borderWidth={1}>
                        <CardBody p={5} textAlign="center">
                            <Box w="48px" h="48px" borderRadius="full" bg="var(--brand-primary)" color="white" display="flex" alignItems="center" justifyContent="center" mx="auto" mb={3}>
                                <Box as={FiEye} size="24px" />
                            </Box>
                            <Text fontSize="20px" fontWeight="600" color="#000000" mb={1}>Page Views</Text>
                            <Text color="gray.600">Automatically tracked</Text>
                        </CardBody>
                    </Card>

                    <Card shadow="md" bg="#f0fdf4" borderColor="green.200" borderWidth={1}>
                        <CardBody p={5} textAlign="center">
                            <Box w="48px" h="48px" borderRadius="full" bg="green.500" color="white" display="flex" alignItems="center" justifyContent="center" mx="auto" mb={3}>
                                <Box as={FiTarget} size="24px" />
                            </Box>
                            <Text fontSize="20px" fontWeight="600" color="#000000" mb={1}>Button & Link Clicks</Text>
                            <Text color="gray.600">With data attributes</Text>
                        </CardBody>
                    </Card>

                    <Card shadow="md" bg="purple.50" borderColor="purple.200" borderWidth={1}>
                        <CardBody p={5} textAlign="center">
                            <Box w="48px" h="48px" borderRadius="full" bg="purple.500" color="white" display="flex" alignItems="center" justifyContent="center" mx="auto" mb={3}>
                                <Box as={FiFileText} size="24px" />
                            </Box>
                            <Text fontSize="20px" fontWeight="600" color="#000000" mb={1}>Form Submissions</Text>
                            <Text color="gray.600">With data attributes</Text>
                        </CardBody>
                    </Card>
                </Grid>

                {/* HTML Examples */}
                <VStack spacing={4} align="stretch">
                    <Box className='box-style-card' shadow="sm">
                        <Box>
                            <Text fontSize="20px" fontWeight="600" color="#000000" mb={3}>Page View Tracking Example</Text>
                            <Text color="gray.700" mb={4}>
                                Every page view is automatically tracked with detailed information about the page, referrer, and session context.
                            </Text>
                            <Card shadow="sm" bg="#1E1E1E" color="white">
                                <CardBody p={6}>
                                    <Box as="code" fontFamily="mono" fontSize="sm" whiteSpace="pre">{`{
  "contact_no": "923181018154",
  "event_name": "page_view",
  "properties": {
    "page_url": "https://example.com/products",
    "page_path": "/products",
    "page_title": "Products - Example Store",
    "referrer": "https://google.com",
    "load_time": 1234,
    "session_id": "sess_1234567890_abc123",
    "timestamp": "2026-01-21T10:30:00.000Z"
  }
}`}</Box>
                                </CardBody>
                            </Card>
                        </Box>
                    </Box>

                    <Box className='box-style-card' shadow="sm">
                        <Box >
                            <Text fontSize="20px" fontWeight="600" color="#000000" mb={3}>Click Tracking Example</Text>
                            <Text color="gray.700" mb={4}>
                                Add the <Text as="code" bg="gray.100" px={2} py={1} borderRadius="md" fontFamily="mono">data-track</Text> attribute to any button or link to automatically track clicks.
                            </Text>
                            <Card shadow="sm" bg="#1E1E1E" color="white" mb={4}>
                                <Box p={6}>
                                    <Box as="code" fontFamily="mono" fontSize="sm">{`<button data-track="cta-signup">Sign Up Now</button>`}</Box>
                                </Box>
                            </Card>
                            <Text color="gray.700" mb={2}>
                                If you would like to add custom properties to track, you can use additional data attributes:
                            </Text>
                            <Box as="ul" color="gray.700" mb={4} ml={6} sx={{ listStyleType: 'disc' }}>
                                <Box as="li" mb={1}>
                                    <Text as="code" bg="gray.100" px={2} py={1} borderRadius="md" fontFamily="mono">data-track</Text> defines the event name
                                </Box>
                                <Box as="li" mb={1}>
                                    <Text as="code" bg="gray.100" px={2} py={1} borderRadius="md" fontFamily="mono">data-track-location</Text> and <Text as="code" bg="gray.100" px={2} py={1} borderRadius="md" fontFamily="mono">data-track-color</Text> are custom properties where "location" and "color" are the property names
                                </Box>
                                <Box as="li">
                                    You can add as many properties as you would like to track using the pattern <Text as="code" bg="gray.100" px={2} py={1} borderRadius="md" fontFamily="mono">data-track-[property-name]</Text>
                                </Box>
                            </Box>
                            <Card shadow="sm" bg="#1E1E1E" color="white" mb={6}>
                                <CardBody p={6}>
                                    <Box as="code" fontFamily="mono" fontSize="sm">{`<button class="btn btn-primary" data-track="cta-add-to-cart" data-track-location="Top" data-track-color="blue">Add to Cart</button>`}</Box>
                                </CardBody>
                            </Card>

                            <Text fontSize="20px" fontWeight="600" color="#000000" mb={3}>Link Click Tracking</Text>
                            <Card shadow="sm" bg="#1E1E1E" color="white" mb={6}>
                                <CardBody p={6}>
                                    <Box as="code" fontFamily="mono" fontSize="sm">{`<a href="/pricing" data-track="nav-pricing">View Pricing</a>`}</Box>
                                </CardBody>
                            </Card>

                            <Text fontSize="20px" fontWeight="600" color="#000000" mb={3}>Banner Click Tracking</Text>
                            <Card shadow="sm" bg="#1E1E1E" color="white" mb={6}>
                                <CardBody p={6}>
                                    <Box as="code" fontFamily="mono" fontSize="sm">{`<div data-track="banner-click">Promotional Banner</div>`}</Box>
                                </CardBody>
                            </Card>

                            <Text fontSize="20px" fontWeight="600" color="#000000" mb={3}>Tracked Event Example:</Text>
                            <Card shadow="sm" bg="#1E1E1E" color="white">
                                <CardBody p={6}>
                                    <Box as="code" fontFamily="mono" fontSize="sm" whiteSpace="pre">{`{
  "contact_no": "923181018154",
  "event_name": "element_click",
  "properties": {
    "element": "button",
    "text": "Sign Up Now",
    "track_id": "cta-signup",
    "x": 450,
    "y": 200
  }
}`}</Box>
                                </CardBody>
                            </Card>
                        </Box>
                    </Box>

                    <Box className='box-style-card' shadow="sm">
                        <Box >
                            <Text fontSize="20px" fontWeight="600" color="#000000" mb={3}>Form Tracking Example</Text>
                            <Text color="gray.700" mb={4}>
                                Add the <Text as="code" bg="gray.100" px={2} py={1} borderRadius="md" fontFamily="mono">data-track-form</Text> attribute to any form to track submissions automatically.
                            </Text>
                            <Card shadow="sm" bg="#1E1E1E" color="white" mb={4}>
                                <CardBody p={6}>
                                    <Box as="code" fontFamily="mono" fontSize="sm" whiteSpace="pre">{`<form data-track-form id="contact-form" action="/submit">
  <input type="email" name="email" required>
  <input type="text" name="name" required>
  <button type="submit">Submit</button>
</form>`}</Box>
                                </CardBody>
                            </Card>
                            <Text color="gray.700" mb={4}>Tracked event data:</Text>
                            <Card shadow="sm" bg="#1E1E1E" color="white">
                                <CardBody p={6}>
                                    <Box as="code" fontFamily="mono" fontSize="sm" whiteSpace="pre">{`{
  "contact_no": "923181018154",
  "event_name": "form_submit",
  "properties": {
    "form_id": "contact-form",
    "form_action": "/submit",
    "form_method": "POST"
  }
}`}</Box>
                                </CardBody>
                            </Card>
                        </Box>
                    </Box>
                </VStack>
            </Box>

            {/* User Identification */}
            <Box>
                <Text fontSize="20px" fontWeight="600" color="#000000" my="0px" >User Identification</Text>
                <Box h="1px" bg="#EAEAEA" mb={4} />
                <Text color="gray.700" mb={6}>
                    Link anonymous sessions to known users when they log in or provide their contact information.
                </Text>

                <VStack spacing={4} align="stretch">
                    <Box className='box-style-card' shadow="sm">
                        <Box >
                            <Text fontSize="20px" fontWeight="600" color="#000000" mb={3}>Identify Once</Text>
                            <Text color="gray.700" mb={4}>
                                Call this method when a user logs in or signs up to associate their session with their identity.
                            </Text>
                            <Card shadow="sm" bg="#1E1E1E" color="white" position="relative">
                                <Button
                                    position="absolute"
                                    top={3}
                                    right={3}
                                    size="sm"
                                    bg="whiteAlpha.200"
                                    color="white"
                                    _hover={{ bg: 'whiteAlpha.300' }}
                                    leftIcon={copiedSection === 'identify' ? <Box as={FiCheck} /> : <Box as={FiCopy} />}
                                    onClick={() => copyToClipboard(identifyOnce, 'identify')}
                                >
                                    {copiedSection === 'identify' ? 'Copied!' : 'Copy'}
                                </Button>
                                <CardBody pt={10}>
                                    <Box as="pre" fontSize="sm" overflowX="auto">
                                        <code>{identifyOnce}</code>
                                    </Box>
                                </CardBody>
                            </Card>
                        </Box>
                    </Box>

                    <Box className='box-style-card' shadow="sm">
                        <Box>
                            <Text fontSize="20px" fontWeight="600" color="#000000" mb={3}>Override for Single Event</Text>
                            <Text color="gray.700" mb={4}>
                                Track an event for a specific contact number without changing the session identity.
                            </Text>
                            <Card shadow="sm" bg="#1E1E1E" color="white" position="relative">
                                <Button
                                    position="absolute"
                                    top={3}
                                    right={3}
                                    size="sm"
                                    bg="whiteAlpha.200"
                                    color="white"
                                    _hover={{ bg: 'whiteAlpha.300' }}
                                    leftIcon={copiedSection === 'override' ? <Box as={FiCheck} /> : <Box as={FiCopy} />}
                                    onClick={() => copyToClipboard(overrideSingleEvent, 'override')}
                                >
                                    {copiedSection === 'override' ? 'Copied!' : 'Copy'}
                                </Button>
                                <CardBody pt={10}>
                                    <Box as="pre" fontSize="sm" overflowX="auto">
                                        <code>{overrideSingleEvent}</code>
                                    </Box>
                                </CardBody>
                            </Card>
                        </Box>
                    </Box>
                </VStack>
            </Box>

            {/* E-Commerce Journey */}
            <Box>
                <Text fontSize="20px" fontWeight="600" color="#000000" my="0px" >E-Commerce & User Journey Examples</Text>
                <Box h="1px" bg="#EAEAEA" mb={4} />
                <Text color="gray.700" mb={6}>
                    Track the complete customer journey from product discovery to purchase completion.
                </Text>

                <VStack spacing={4} align="stretch">
                    {[
                        {
                            title: 'Product Viewed',
                            color: '#2b7fff',
                            code: `EventTracker.track('product_viewed', {
  product_id: 'SKU-123',
  product_name: 'Wireless Headphones',
  price: 79.99
});`
                        },
                        {
                            title: 'Added to Cart',
                            color: '#00c951',
                            code: `EventTracker.track('add_to_cart', {
  product_id: 'SKU-123',
  quantity: 1,
  cart_value: 79.99
});`
                        },
                        {
                            title: 'Removed from Cart',
                            color: '#fb2c36',
                            code: `EventTracker.track('product_removed_from_cart', {
  product_id: 'PROD-456',
  product_name: 'Running Shoes'
});`
                        },
                        {
                            title: 'Cart Abandoned (after 10 minutes of inactivity)',
                            color: '#f0b100',
                            code: `setTimeout(() => {
  EventTracker.track('cart_abandoned', {
    email: 'user@example.com',
    cart_value: 179.98,
    items_count: 2
  });
}, 600000); // 10 minutes`
                        },
                        {
                            title: 'Checkout Started',
                            color: '#ff6900',
                            code: `EventTracker.track('checkout_started', {
  cart_value: 159.98,
  items_count: 2
});`
                        },
                        {
                            title: 'Purchase Completed',
                            color: '#ad46ff',
                            code: `EventTracker.track('purchase', {
  order_id: 'ORD-789',
  amount: 159.98,
  currency: 'USD'
});`
                        }
                    ].map((step, index) => (
                        <HStack key={index} align="stretch" spacing={4}>
                            <VStack spacing={0} align="center">
                                <Box
                                    w="40px"
                                    h="40px"
                                    borderRadius="full"
                                    bg={step.color}
                                    color="white"
                                    display="flex"
                                    alignItems="center"
                                    justifyContent="center"
                                    flexShrink={0}
                                    fontWeight="semibold"
                                >
                                    {index + 1}
                                </Box>

                                {index < 5 && (
                                    <Box
                                        w="2px"
                                        flex="1"
                                        bg="gray.300"
                                    />
                                )}
                            </VStack>

                            <Box className="box-style-card" shadow="sm" flex={1}>
                                <Text fontSize="20px" fontWeight="600" color="#000000" mb={3}>{step.title}</Text>

                                <Card shadow="sm" bg="#1E1E1E" color="white">
                                    <CardBody p={6}>
                                        <Box as="code" fontFamily="mono" fontSize="sm" whiteSpace="pre">
                                            {step.code}
                                        </Box>
                                    </CardBody>
                                </Card>
                            </Box>
                        </HStack>

                    ))}
                </VStack>
            </Box>

            {/* User Journey Tracking Examples */}
            <Box>
                <Text fontSize="20px" fontWeight="600" color="#000000" my="0px" >User Journey Tracking Examples</Text>
                <Box h="1px" bg="#EAEAEA" mb={4} />
                <Text color="gray.700" mb={6}>
                    Track key user lifecycle events from registration to subscription management.
                </Text>

                <Grid templateColumns={{ base: '1fr', md: 'repeat(2, 1fr)' }} gap={4}>
                    {/* User Registration */}
                    <Box className='box-style-card' shadow="sm">
                        <Box >
                            <Text fontSize="20px" fontWeight="600" color="#000000" mb={2}>User Registration</Text>
                            <Text color="gray.600" mb={4}>Identify and track new user signups with their profile information.</Text>
                            <Card shadow="sm" bg="#1E1E1E" color="white">
                                <CardBody p={6}>
                                    <Box as="code" fontFamily="mono" fontSize="sm" whiteSpace="pre">{`EventTracker.identify('923181018154', {
  email: 'newuser@example.com',
  name: 'Jane Smith',
  signup_date: new Date().toISOString()
});`}</Box>
                                </CardBody>
                            </Card>
                        </Box>
                    </Box>

                    {/* Profile Updated */}
                    <Box className='box-style-card' shadow="sm">
                        <Box >
                            <Text fontSize="20px" fontWeight="600" color="#000000" mb={2}>Profile Updated</Text>
                            <Text color="gray.600" mb={4}>Track when users update their profile information.</Text>
                            <Card shadow="sm" bg="#1E1E1E" color="white">
                                <CardBody p={6}>
                                    <Box as="code" fontFamily="mono" fontSize="sm" whiteSpace="pre">{`EventTracker.track('profile_updated', {
  fields_updated: ['phone', 'address'],
  plan: 'premium'
});`}</Box>
                                </CardBody>
                            </Card>
                        </Box>
                    </Box>

                    {/* Subscription Purchased */}
                    <Box className='box-style-card' shadow="sm">
                        <Box >
                            <Text fontSize="20px" fontWeight="600" color="#000000" mb={2}>Subscription Purchased</Text>
                            <Text color="gray.600" mb={4}>Track when users start a new subscription.</Text>
                            <Card shadow="sm" bg="#1E1E1E" color="white">
                                <CardBody p={6}>
                                    <Box as="code" fontFamily="mono" fontSize="sm" whiteSpace="pre">{`EventTracker.track('subscription_started', {
  plan: 'premium',
  billing_cycle: 'monthly',
  amount: 29.99
});`}</Box>
                                </CardBody>
                            </Card>
                        </Box>
                    </Box>

                    {/* Subscription Cancelled */}
                    <Box className='box-style-card' shadow="sm">
                        <Box >
                            <Text fontSize="20px" fontWeight="600" color="#000000" mb={2}>Subscription Cancelled</Text>
                            <Text color="gray.600" mb={4}>Track subscription cancellations with reasons.</Text>
                            <Card shadow="sm" bg="#1E1E1E" color="white">
                                <CardBody p={6}>
                                    <Box as="code" fontFamily="mono" fontSize="sm" whiteSpace="pre">{`EventTracker.track('subscription_cancelled', {
  plan: 'premium',
  cancellation_reason: 'too_expensive'
});`}</Box>
                                </CardBody>
                            </Card>
                        </Box>
                    </Box>
                </Grid>
            </Box>
            <Box>
                <Text fontSize="20px" fontWeight="600" color="#000000" my="0px">Integration Examples</Text>
                <Box h="1px" bg="#EAEAEA" mb={4} />

                <Tabs>
                    <TabList
                        bg="#f1f5f9"
                        p="1"
                        borderRadius="full"
                        display="flex"
                        justifyContent="space-between"
                    >
                        <Tab flex="1"
                            _selected={{ bg: "white", boxShadow: "sm", fontWeight: "600" }}
                            borderRadius="full"
                            fontSize="sm">React.JS</Tab>
                        <Tab flex="1"
                            _selected={{ bg: "white", boxShadow: "sm", fontWeight: "600" }}
                            borderRadius="full"
                            fontSize="sm">Vue.JS</Tab>
                        <Tab flex="1"
                            _selected={{ bg: "white", boxShadow: "sm", fontWeight: "600" }}
                            borderRadius="full"
                            fontSize="sm">WordPress (PHP)</Tab>
                    </TabList>

                    <TabPanels my={4}>
                        <TabPanel p={0}>
                            <VStack spacing={6} align="stretch">
                                <Box className='box-style-card' shadow="sm">
                                    <Box>
                                        <Text fontSize="20px" fontWeight="600" color="#000000" mb={3}>Next.js App-wide Page Tracking</Text>
                                        <Card shadow="sm" bg="#1E1E1E" color="white" position="relative" borderRadius="md">
                                            <Button
                                                position="absolute"
                                                top={3}
                                                right={3}
                                                size="sm"
                                                bg="whiteAlpha.200"
                                                color="white"
                                                _hover={{ bg: 'whiteAlpha.300' }}
                                                leftIcon={copiedSection === 'react-app' ? <Box as={FiCheck} /> : <Box as={FiCopy} />}
                                                onClick={() => copyToClipboard(`import { useEffect } from 'react';
import { useRouter } from 'next/router';

function MyApp({ Component, pageProps }) {
  const router = useRouter();

  useEffect(() => {
    // Track page views on route change
    const handleRouteChange = (url) => {
      EventTracker.page(url);
    };

    router.events.on('routeChangeComplete', handleRouteChange);

    return () => {
      router.events.off('routeChangeComplete', handleRouteChange);
    };
  }, [router.events]);

  return <Component {...pageProps} />;
}`, 'react-app')}
                                            >
                                                {copiedSection === 'react-app' ? 'Copied!' : 'Copy'}
                                            </Button>
                                            <Box pt={10} pb={4} px={4}>
                                                <Box as="pre" fontSize="sm" overflowX="auto">
                                                    <code>{`import { useEffect } from 'react';
import { useRouter } from 'next/router';

function MyApp({ Component, pageProps }) {
  const router = useRouter();

  useEffect(() => {
    // Track page views on route change
    const handleRouteChange = (url) => {
      EventTracker.page(url);
    };

    router.events.on('routeChangeComplete', handleRouteChange);

    return () => {
      router.events.off('routeChangeComplete', handleRouteChange);
    };
  }, [router.events]);

  return <Component {...pageProps} />;
}`}</code>
                                                </Box>
                                            </Box>
                                        </Card>
                                    </Box>
                                </Box>

                                <Box className='box-style-card' shadow="sm">
                                    <Box>
                                        <Text fontSize="20px" fontWeight="600" color="#000000" mb={3}>Tracking Product Views & Add to Cart Logic</Text>
                                        <Card shadow="sm" bg="#1E1E1E" color="white" position="relative" borderRadius="md">
                                            <Button
                                                position="absolute"
                                                top={3}
                                                right={3}
                                                size="sm"
                                                bg="whiteAlpha.200"
                                                color="white"
                                                _hover={{ bg: 'whiteAlpha.300' }}
                                                leftIcon={copiedSection === 'react-component' ? <Box as={FiCheck} /> : <Box as={FiCopy} />}
                                                onClick={() => copyToClipboard(`function ProductPage({ product }) {
  useEffect(() => {
    EventTracker.track('product_viewed', {
      product_id: product.id,
      product_name: product.name,
      price: product.price
    });
  }, [product]);

  const handleAddToCart = () => {
    EventTracker.track('product_added_to_cart', {
      product_id: product.id,
      product_name: product.name,
      price: product.price,
      quantity: 1
    });

    // Your add to cart logic
  };

  return (
    <button onClick={handleAddToCart}>Add to Cart</button>
  );
}`, 'react-component')}
                                            >
                                                {copiedSection === 'react-component' ? 'Copied!' : 'Copy'}
                                            </Button>
                                            <Box pt={10} pb={4} px={4}>
                                                <Box as="pre" fontSize="sm" overflowX="auto">
                                                    <code>{`function ProductPage({ product }) {
  useEffect(() => {
    EventTracker.track('product_viewed', {
      product_id: product.id,
      product_name: product.name,
      price: product.price
    });
  }, [product]);

  const handleAddToCart = () => {
    EventTracker.track('product_added_to_cart', {
      product_id: product.id,
      product_name: product.name,
      price: product.price,
      quantity: 1
    });

    // Your add to cart logic
  };

  return (
    <button onClick={handleAddToCart}>Add to Cart</button>
  );
}`}</code>
                                                </Box>
                                            </Box>
                                        </Card>
                                    </Box>
                                </Box>
                            </VStack>
                        </TabPanel>

                        <TabPanel p={0}>
                            <Box className='box-style-card' shadow="sm">
                                <Box>
                                    <Text fontSize="20px" fontWeight="600" color="#000000" mb={3}>Vue.js Component with User Identification</Text>
                                    <Card shadow="sm" bg="#1E1E1E" color="white" position="relative" borderRadius="md">
                                        <Button
                                            position="absolute"
                                            top={3}
                                            right={3}
                                            size="sm"
                                            bg="whiteAlpha.200"
                                            color="white"
                                            _hover={{ bg: 'whiteAlpha.300' }}
                                            leftIcon={copiedSection === 'vue-component' ? <Box as={FiCheck} /> : <Box as={FiCopy} />}
                                            onClick={() => copyToClipboard(`<template>
  <button @click="trackPurchase">Complete Purchase</button>
</template>

<script>
export default {
  mounted() {
    // Identify user when component mounts
    if (this.$auth.user) {
      EventTracker.identify(this.$auth.user.phone, {
        email: this.$auth.user.email,
        name: this.$auth.user.name
      });
    }
  },
  methods: {
    trackPurchase() {
      EventTracker.track('purchase', {
        order_id: this.orderId,
        amount: this.totalAmount,
        items_count: this.cartItems.length
      });

      // Complete purchase logic
    }
  }
}
</script>`, 'vue-component')}
                                        >
                                            {copiedSection === 'vue-component' ? 'Copied!' : 'Copy'}
                                        </Button>
                                        <Box pt={10} pb={4} px={4}>
                                            <Box as="pre" fontSize="sm" overflowX="auto">
                                                <code>{`<template>
  <button @click="trackPurchase">Complete Purchase</button>
</template>

<script>
export default {
  mounted() {
    // Identify user when component mounts
    if (this.$auth.user) {
      EventTracker.identify(this.$auth.user.phone, {
        email: this.$auth.user.email,
        name: this.$auth.user.name
      });
    }
  },
  methods: {
    trackPurchase() {
      EventTracker.track('purchase', {
        order_id: this.orderId,
        amount: this.totalAmount,
        items_count: this.cartItems.length
      });

      // Complete purchase logic
    }
  }
}
</script>`}</code>
                                            </Box>
                                        </Box>
                                    </Card>
                                </Box>
                            </Box>
                        </TabPanel>

                        <TabPanel p={0}>
                            <Box className='box-style-card' shadow="sm">
                                <Box>
                                    <Text fontSize="20px" fontWeight="600" color="#000000" mb={3}>WordPress Theme Integration</Text>
                                    <Text color="gray.700" mb={4}>
                                        In your theme's header.php or footer.php
                                    </Text>
                                    <Card shadow="sm" bg="#1E1E1E" color="white" position="relative" borderRadius="md">
                                        <Button
                                            position="absolute"
                                            top={3}
                                            right={3}
                                            size="sm"
                                            bg="whiteAlpha.200"
                                            color="white"
                                            _hover={{ bg: 'whiteAlpha.300' }}
                                            leftIcon={copiedSection === 'wordpress' ? <Box as={FiCheck} /> : <Box as={FiCopy} />}
                                            onClick={() => copyToClipboard(`<!-- In your theme's header.php or footer.php -->
<script src="${trackerUrl}"
  data-api-key="<?php echo get_option('event_tracker_api_key'); ?>"
  <?php if (is_user_logged_in()): ?>
  data-contact-no="<?php echo get_user_meta(get_current_user_id(), 'phone', true); ?>"
  <?php endif; ?>
></script>

<?php if (is_user_logged_in()): ?>
<script>
  EventTracker.identify(
    '<?php echo get_user_meta(get_current_user_id(), 'phone', true); ?>',
    {
      email: '<?php echo wp_get_current_user()->user_email; ?>',
      name: '<?php echo wp_get_current_user()->display_name; ?>'
    }
  );
</script>
<?php endif; ?>`, 'wordpress')}
                                        >
                                            {copiedSection === 'wordpress' ? 'Copied!' : 'Copy'}
                                        </Button>
                                        <Box pt={10} pb={4} px={4}>
                                            <Box as="pre" fontSize="sm" overflowX="auto">
                                                <code>{`<!-- In your theme's header.php or footer.php -->
<script src="${trackerUrl}"
  data-api-key="<?php echo get_option('event_tracker_api_key'); ?>"
  <?php if (is_user_logged_in()): ?>
  data-contact-no="<?php echo get_user_meta(get_current_user_id(), 'phone', true); ?>"
  <?php endif; ?>
></script>

<?php if (is_user_logged_in()): ?>
<script>
  EventTracker.identify(
    '<?php echo get_user_meta(get_current_user_id(), 'phone', true); ?>',
    {
      email: '<?php echo wp_get_current_user()->user_email; ?>',
      name: '<?php echo wp_get_current_user()->display_name; ?>'
    }
  );
</script>
<?php endif; ?>`}</code>
                                            </Box>
                                        </Box>
                                    </Card>
                                </Box>
                            </Box>
                        </TabPanel>
                    </TabPanels>
                </Tabs>
            </Box>

           <Box>
        <Box mb={4}>
          <Text fontSize="2xl" mb={2} fontWeight="semibold">Configuration Options</Text>
          <Box h="1px" bg="#EAEAEA" />
        </Box>

        <VStack spacing={6} align="stretch">
          <Box className='box-style-card' shadow="sm">
            <Box >
              <Text fontWeight="semibold" mb={3}>Adjust Batching</Text>
              <Card shadow="sm" bg="#1E1E1E" color="white" position="relative">
                <CardBody p={0}>
                  <Button
                    position="absolute"
                    top={3}
                    right={3}
                    size="sm"
                    bg="whiteAlpha.200"
                    color="white"
                    _hover={{ bg: 'whiteAlpha.300' }}
                    leftIcon={copiedSection === 'config-batching' ? <FiCheck /> : <FiCopy />}
                    onClick={() => copyToClipboard(`const CONFIG = {
  batchSize: 100,        // Number of events per batch
  batchTimeout: 3000,    // Milliseconds to wait before sending
  maxQueueSize: 1000     // Max events in memory
};`, 'config-batching')}
                  >
                    {copiedSection === 'config-batching' ? 'Copied!' : 'Copy'}
                  </Button>
                  <Box as="pre" p={6} pt={10} overflowX="auto">
                    <Box as="code" color="#E5E7EB">{`const CONFIG = {
  batchSize: 100,        // Number of events per batch
  batchTimeout: 3000,    // Milliseconds to wait before sending
  maxQueueSize: 1000     // Max events in memory
};`}</Box>
                  </Box>
                </CardBody>
              </Card>
            </Box>
          </Box>

          <Box className='box-style-card' shadow="sm">
            <Box >
              <Text fontWeight="semibold" mb={3}>Event Sampling</Text>
              <Text color="gray.700" mb={4}>
                Control which events are sampled (to reduce load):
              </Text>
              <Card shadow="sm" bg="#1E1E1E" color="white" position="relative">
                <CardBody p={0}>
                  <Button
                    position="absolute"
                    top={3}
                    right={3}
                    size="sm"
                    bg="whiteAlpha.200"
                    color="white"
                    _hover={{ bg: 'whiteAlpha.300' }}
                    leftIcon={copiedSection === 'config-sampling' ? <FiCheck /> : <FiCopy />}
                    onClick={() => copyToClipboard(`const SAMPLING_RULES = {
  'page_view': 1.0,         // 100% - track all
  'purchase': 1.0,          // 100% - track all
  'cart_abandoned': 1.0,    // 100% - track all
  'mouse_move': 0.01,       // 1% - sample only
  'scroll': 0.05            // 5% - sample only
};`, 'config-sampling')}
                  >
                    {copiedSection === 'config-sampling' ? 'Copied!' : 'Copy'}
                  </Button>
                  <Box as="pre" p={6} pt={10} overflowX="auto">
                    <Box as="code" color="#E5E7EB">{`const SAMPLING_RULES = {
  'page_view': 1.0,         // 100% - track all
  'purchase': 1.0,          // 100% - track all
  'cart_abandoned': 1.0,    // 100% - track all
  'mouse_move': 0.01,       // 1% - sample only
  'scroll': 0.05            // 5% - sample only
};`}</Box>
                  </Box>
                </CardBody>
              </Card>
            </Box>
          </Box>
        </VStack>
      </Box>
            <Box>
                <Text fontSize="20px" fontWeight="600" color="#000000" my="0px" >Session & Identity Model</Text>
                <Box h="1px" bg="#EAEAEA" mb={4} />
                <Card shadow="sm">
                    <CardBody p={0}>
                        <TableContainer>
                            <Table variant="simple">
                                <Thead bg="#F7F7F7">
                                    <Tr>
                                        <Th fontSize="14px" fontWeight="600" color="black" textTransform="none">Identifier</Th>
                                        <Th fontSize="14px" fontWeight="600" color="black" textTransform="none">Purpose</Th>
                                        <Th fontSize="14px" fontWeight="600" color="black" textTransform="none">Changes</Th>
                                        <Th fontSize="14px" fontWeight="600" color="black" textTransform="none">Use Case</Th>
                                        <Th fontSize="14px" fontWeight="600" color="black" textTransform="none">Example</Th>
                                    </Tr>
                                </Thead>
                                <Tbody>
                                    <Tr borderBottom="1px" borderColor="#EAEAEA">
                                        <Td fontFamily="mono">distinct_id</Td>
                                        <Td>Persistent anonymous user identifier</Td>
                                        <Td>Never (unless browser data cleared)</Td>
                                        <Td>Track user across multiple sessions and visits</Td>
                                        <Td fontFamily="mono" color="gray.600">uuid-abc-123-def</Td>
                                    </Tr>
                                    <Tr borderBottom="1px" borderColor="#EAEAEA">
                                        <Td fontFamily="mono">session_id</Td>
                                        <Td>Single browsing session identifier</Td>
                                        <Td>When browser closes or 30 min inactivity</Td>
                                        <Td>Unique identifier for browsing session</Td>
                                        <Td fontFamily="mono" color="gray.600">session-xyz-789</Td>
                                    </Tr>
                                    <Tr>
                                        <Td fontFamily="mono">contact_no</Td>
                                        <Td>Known user identifier (phone number)</Td>
                                        <Td>When user identifies (e.g., at checkout)</Td>
                                        <Td>Link anonymous events to known user</Td>
                                        <Td fontFamily="mono" color="gray.600">923181018154</Td>
                                    </Tr>
                                </Tbody>
                            </Table>
                        </TableContainer>
                    </CardBody>
                </Card>
                <Alert mt={4} bg="var(--brand-tint)" borderColor="var(--brand-tint-strong)" borderWidth={1} borderRadius={"md"}>
                    <AlertDescription color="#193cb8">
                        💡 <Text as="strong">Note:</Text> Anonymous events automatically merge with the user profile once they are identified.
                    </AlertDescription>
                </Alert>
            </Box>

            {/* Debugging & Testing */}
            <Box>
                <Text fontSize="20px" fontWeight="600" color="#000000" my="0px" >Debugging & Testing</Text>
                <Box h="1px" bg="#EAEAEA" mb={4} />

                <Box className='box-style-card' shadow="sm" mb={4}>
                    <Box >
                        <Text fontSize="20px" fontWeight="600" color="#000000" mb={3}>Debug Panel</Text>
                        <Text color="gray.700" mb={4}>
                            Check tracker status and debug information in the browser console:
                        </Text>
                        <Card shadow="sm" bg="#1E1E1E" color="white" mb={4}>
                            <CardBody p={6}>
                                <Box as="code" fontFamily="mono" fontSize="sm">{`console.log(EventTracker.status());`}</Box>
                            </CardBody>
                        </Card>
                    </Box>
                </Box>
            </Box>
        </VStack>
    );
};